import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  DefaultPayment,
  JsFieldPaymentWrapper,
  PaymentMethodWrapper,
  SelectPayment,
  TitleWrapper,
  BlockWrapper
} from './PaymentMethodForm.style';
import Button from 'components/ui-kits/Button';
import { Checkbox } from 'components/ui-kits/Form/Checkbox';
import DisplayText from 'components/ui-kits/DisplayText';
import {
  CartInStore,
  CashIcon,
  CreditCardIcon,
  RestaurantTicketIcon
} from 'components/ui-kits/Icon';
import { IPaymentMethod } from 'types/PaymentMethod';
import { AllowPaymentMethods } from 'types/Merchant';
import ButtonIcon from 'components/ui-kits/ButtonIcon';
import { IconNames } from 'components/ui-kits/Icon/Icon';
import xor from 'lodash.xor';

interface IPaymentMethodForm {
  mode: 'list' | 'edit';
  onChangeMode(m: 'list' | 'edit'): void;
  isCardPaymentAllowed: boolean;
  allowPaymentMethod: AllowPaymentMethods[];
  onSave(dataSave: AllowPaymentMethods[]): void;
}

const PAYMENT_METHODS: IPaymentMethod[] = [
  { label: 'Espèce', icon: <CashIcon />, paymentCode: 'by_cash' },
  {
    label: 'Carte sur place',
    icon: <CartInStore />,
    paymentCode: 'by_vacation_ticket'
  },
  {
    label: 'Titres Restaurant',
    icon: <RestaurantTicketIcon />,
    paymentCode: 'by_restaurant_ticket'
  },
  { label: 'Carte Bancaire', icon: <CreditCardIcon />, paymentCode: 'by_card' }
];

const EnableCardPaymentInfo = (
  <DisplayText
    label="(Pour activer le paiement en ligne, rendez-vous dans la section Compte)"
    fontSize="M"
    margin="0 0 0 0.4rem"
  />
);

const PaymentMethodForm = ({
  mode,
  onChangeMode,
  allowPaymentMethod,
  onSave,
  isCardPaymentAllowed
}: IPaymentMethodForm) => {
  const {
    register,
    getValues,
    formState: { isDirty },
    reset
  } = useForm();

  useEffect(() => {
    reset();
  }, [allowPaymentMethod, reset]);

  const defaultPaymentCodes = isCardPaymentAllowed ? ['by_card'] : ['by_cash'];

  const defaultPaymentOptions = PAYMENT_METHODS.filter((item) =>
    defaultPaymentCodes.includes(item.paymentCode)
  );

  const handleSubmit = () => {
    onSave(getValues('selectPayment'));
  };
  const displayListPayment = (
    arrPayment: {
      label: string;
      icon: React.ReactNode;
      paymentCode: AllowPaymentMethods;
    }[]
  ) => {
    if (Array.isArray(allowPaymentMethod)) {
      return arrPayment.map((item, index: number) => {
        const check = allowPaymentMethod.some(
          (res) => res === item.paymentCode
        );
        const isCardPaymentDisabled =
          item.paymentCode === 'by_card' && !isCardPaymentAllowed;

        if (mode === 'list') {
          return (
            <BlockWrapper key={index}>
              <JsFieldPaymentWrapper mode={mode} selected={check}>
                {item.icon}
                <DisplayText label={item.label} fontSize="M" className="text" />
                {isCardPaymentDisabled && EnableCardPaymentInfo}
              </JsFieldPaymentWrapper>
            </BlockWrapper>
          );
        } else {
          if (isCardPaymentDisabled) {
            return (
              <BlockWrapper
                key={index}
                style={{ marginLeft: '5.5rem', cursor: 'default' }}>
                <JsFieldPaymentWrapper mode={mode} selected={false}>
                  {item.icon}
                  <DisplayText label={item.label} fontSize="M" />
                  {EnableCardPaymentInfo}
                </JsFieldPaymentWrapper>
              </BlockWrapper>
            );
          }

          return (
            <BlockWrapper key={index}>
              <Checkbox
                name="selectPayment"
                register={register}
                defaultChecked={check}
                value={item.paymentCode}>
                <JsFieldPaymentWrapper mode={mode} selected={check}>
                  {item.icon}
                  <DisplayText label={item.label} fontSize="M" />
                </JsFieldPaymentWrapper>
              </Checkbox>
            </BlockWrapper>
          );
        }
      });
    }
  };

  return (
    <PaymentMethodWrapper>
      <TitleWrapper>
        <div className="wrapper">
          <DisplayText
            label="Méthode de paiement"
            fontSize="XXL"
            fontWeight="LIGHT"
          />
          {mode === 'list' && (
            <Button
              className="edit"
              label="Modifier"
              variant="link"
              isFluid={false}
              isUpperCase={false}
              fontWeight="BOLD"
              onClick={() => onChangeMode('edit')}
            />
          )}
        </div>
        {mode === 'edit' && (
          <div className="action-wrapper">
            <Button
              label="Enregistrer"
              isFluid={false}
              variant="primary"
              fontWeight="BOLD"
              letterSpacing="S"
              onClick={handleSubmit}
              isDisabled={!isDirty}
            />
            <ButtonIcon
              onClick={() => onChangeMode('list')}
              icon={IconNames.Close}
              widthIcon={50}
              variant="square"
              type="button"
            />
          </div>
        )}
      </TitleWrapper>
      <DefaultPayment>
        <DisplayText
          label="Méthodes de paiement par défaut"
          fontSize="S"
          fontWeight="BOLD"
          color="BLACK_4"
          className="title"
        />
        {defaultPaymentOptions.map((item) => (
          <BlockWrapper key={item.paymentCode}>
            <JsFieldPaymentWrapper mode={mode} selected>
              {item.icon}
              <DisplayText label={item.label} fontSize="M" className="text" />
            </JsFieldPaymentWrapper>
          </BlockWrapper>
        ))}
      </DefaultPayment>
      <SelectPayment>
        <DisplayText
          label="Sélectionnez les autre méthodes de paiement que vous souhaitez accepter"
          fontSize="S"
          fontWeight="BOLD"
          color="BLACK_4"
          className="title"
        />
        {/* display the rest of the payment options of the list */}
        {displayListPayment(xor(defaultPaymentOptions, PAYMENT_METHODS))}
      </SelectPayment>
    </PaymentMethodWrapper>
  );
};

export default PaymentMethodForm;
